/**
 * AdminDashboard Page
 * 
 * Main admin panel dashboard showing overview statistics and quick actions
 * 
 * Features:
 * - Real-time statistics display
 * - Quick navigation to other admin sections
 * - Admin user info display
 * - Logout functionality
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminStats from '../components/AdminStats';
import '../assets/styles/AdminDashboard.css';

// Use environment variable for API URL, fallback to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminUser, setAdminUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load admin user info
    const userInfo = localStorage.getItem('adminUser');
    if (userInfo) {
      setAdminUser(JSON.parse(userInfo));
    }

    // Fetch dashboard statistics
    fetchDashboardStats();
  }, []);

  /**
   * Fetch dashboard statistics from backend
   */
  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${API_URL}/admin/dashboard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        setError('Failed to load statistics');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Unable to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle admin logout
   */
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  /**
   * Navigate to users management
   */
  const goToUsers = () => {
    navigate('/admin/users');
  };

  /**
   * Navigate to charts management
   */
  const goToCharts = () => {
    navigate('/admin/charts');
  };

  return (
    <div className="admin-dashboard-container">
      {/* Admin Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>Admin Dashboard</h1>
          <p>Welcome back, {adminUser?.fullName || 'Administrator'}</p>
        </div>
        <div className="admin-header-right">
          <button className="admin-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      {/* Dashboard Content */}
      <main className="admin-main-content">
        {/* Statistics Section */}
        <section className="admin-section">
          <h2>Overview Statistics</h2>
          <AdminStats 
            stats={stats} 
            isLoading={isLoading} 
            error={error} 
          />
        </section>

        {/* Quick Actions Section */}
        <section className="admin-section">
          <h2>Quick Actions</h2>
          <div className="admin-actions-grid">
            {/* Manage Users */}
            <div className="admin-action-card" onClick={goToUsers}>
              <div className="action-icon">👥</div>
              <h3>Manage Users</h3>
              <p>View, search, and manage user accounts and roles</p>
            </div>

            {/* Manage Charts */}
            <div className="admin-action-card" onClick={goToCharts}>
              <div className="action-icon">📊</div>
              <h3>Manage Charts</h3>
              <p>Add, edit, or remove dashboard charts</p>
            </div>

            {/* View Audit Logs (Future) */}
            <div className="admin-action-card disabled">
              <div className="action-icon">📝</div>
              <h3>Audit Logs</h3>
              <p>View admin activity and security logs (Coming Soon)</p>
            </div>

            {/* Analytics (Future) */}
            <div className="admin-action-card disabled">
              <div className="action-icon">📈</div>
              <h3>Analytics</h3>
              <p>View detailed analytics and reports (Coming Soon)</p>
            </div>
          </div>
        </section>

        {/* System Info */}
        <section className="admin-section">
          <h2>System Information</h2>
          <div className="system-info-box">
            <div className="info-row">
              <span className="info-label">Admin User:</span>
              <span className="info-value">{adminUser?.username}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Role:</span>
              <span className="info-value badge-admin">{adminUser?.role}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Last Login:</span>
              <span className="info-value">{new Date().toLocaleString()}</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;

